import { useEffect, useState } from "react";
import { Button, Card, Popconfirm, Space, Table, Tag, Tooltip } from "antd";
import { PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, ApiOutlined } from "@ant-design/icons";
import { t } from "i18next";
import SimpleLayout from "../../core/components/layout/SimpleLayout";
import ApiService from "../../core/service/ApiService";
import { useModal } from "../../core/context/ModalContext";
import AppConfig from "../../core/service/AppConfig";
import FirmPosDeviceModal from "./FirmPosDeviceModal";
import PosPairingModal from "./PosPairingModal";
import { FilterActions, SearchCard } from "../../core/components/quick-filter/QuickFilterParts";

export default function FirmPosDeviceApp() {
    const [devices, setDevices] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const { openModal, closeModal } = useModal();

    const load = async () => {
        setLoading(true);
        try {
            const res = await ApiService.FirmPosDevice.GetFirmPosDevices({});
            if (res.success) {
                setDevices(res.data);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = `${t('POS Devices')} - ${AppConfig.getHisTitle()}`;
        load();
    }, []);

    const openEdit = (device) => {
        openModal(
            device ? t('Edit POS Device') : t('New POS Device'),
            <FirmPosDeviceModal device={device} onSuccess={() => { closeModal(); load(); }} />,
            () => { }
        );
    };

    const openPair = (device) => {
        openModal(
            `${t('Pair')} - ${device.name}`,
            <PosPairingModal device={device} onSuccess={() => { closeModal(); load(); }} />,
            () => { }
        );
    };

    const onDelete = async (id) => {
        const res = await ApiService.FirmPosDevice.DeleteFirmPosDevice(id);
        if (res.success) load();
    };

    const v = searchText.trim().toLowerCase();
    const filtered = !v ? devices : devices.filter(d =>
        (d.name || '').toLowerCase().includes(v) ||
        (d.serialNumber || '').toLowerCase().includes(v) ||
        (d.firm?.name || '').toLowerCase().includes(v)
    );

    return (
        <SimpleLayout fullWidth={true}>
            <div className="qf-row qf-row-top">
                <SearchCard value={searchText} onChange={setSearchText} />
            </div>
            <FilterActions>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>{t('New')}</Button>
                <Button size="small" icon={<ReloadOutlined />} onClick={load}>{t('Refresh')}</Button>
            </FilterActions>
            <Card title={t('POS Devices')}>
                <Table
                    rowKey="id"
                    size="small"
                    loading={loading}
                    dataSource={filtered}
                    pagination={false}
                    scroll={{ x: 1000 }}
                    columns={[
                        { dataIndex: 'firm', key: 'firm', title: t('Firm'), render: (val) => val?.name || '-' },
                        { dataIndex: 'name', key: 'name', title: t('Name') },
                        { dataIndex: 'address', key: 'address', title: t('Address'), render: (v) => <Tooltip title={v}><span>{v}</span></Tooltip> },
                        { dataIndex: 'serialNumber', key: 'serialNumber', title: t('Serial Number') },
                        { dataIndex: 'type', key: 'type', title: t('Type'), render: (v) => v || '-' },
                        {
                            dataIndex: 'fingerprint', key: 'fingerprint', title: t('Pairing'),
                            render: (v) => v ? <Tag color="success">{t('Paired')}</Tag> : <Tag color="warning">{t('Not Paired')}</Tag>
                        },
                        { dataIndex: 'transactionSeq', key: 'transactionSeq', title: t('Tx Seq'), width: 80 },
                        {
                            key: 'actions', title: t('#'), width: 220, fixed: 'right',
                            render: (_, row) => (
                                <Space>
                                    <Button size="small" type="primary" ghost icon={<ApiOutlined />} onClick={() => openPair(row)}>{t('Pair')}</Button>
                                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
                                    <Popconfirm title={t('Are you sure?')} onConfirm={() => onDelete(row.id)} okText={t('Yes')} cancelText={t('No')}>
                                        <Button size="small" danger icon={<DeleteOutlined />} />
                                    </Popconfirm>
                                </Space>
                            )
                        },
                    ]}
                />
            </Card>
        </SimpleLayout>
    );
}
